import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Copy, Check, ExternalLink } from 'lucide-react';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  PageHeader,
  StatusPill,
} from '@/components/ui';
import type { WizardCompleteResponse } from '@/api/onboarding';
import { clearWizardStorage } from './hooks/useWizardState';
import { ONBOARDING_BASE_PATH } from './constants';
import { tOnboarding } from '@/i18n/onboarding';

const RESULT_STORAGE_KEY = 'onboarding-result';

type OnboardingResultState = {
  response?: WizardCompleteResponse;
  error?: string;
};

function loadOnboardingResult(): OnboardingResultState | null {
  try {
    const stored = sessionStorage.getItem(RESULT_STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as OnboardingResultState;
  } catch (error) {
    console.warn('Failed to load onboarding result:', error);
    return null;
  }
}

function clearOnboardingResult(): void {
  try {
    sessionStorage.removeItem(RESULT_STORAGE_KEY);
    clearWizardStorage();
  } catch (error) {
    console.warn('Failed to clear onboarding result:', error);
  }
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button
      variant="ghost"
      size="icon-sm"
      onClick={() => void handleCopy()}
      aria-label={`Copy ${value}`}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-success" aria-hidden="true" />
      ) : (
        <Copy className="h-3.5 w-3.5 text-mute" aria-hidden="true" />
      )}
    </Button>
  );
}

function DetailRow({
  label,
  value,
  copyable = false,
}: {
  label: string;
  value: string | number;
  copyable?: boolean;
}) {
  const displayValue = String(value);
  return (
    <div className="flex items-center justify-between border-b border-line py-2.5 last:border-0">
      <span className="text-sm text-ink-2">{label}</span>
      <div className="flex items-center gap-1.5">
        <span className="font-mono text-sm font-medium text-ink">
          {displayValue}
        </span>
        {copyable && <CopyButton value={displayValue} />}
      </div>
    </div>
  );
}

function LinkButton({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center justify-between rounded-control bg-paper px-4 py-3 ring-1 ring-inset ring-line transition-[background-color] duration-[120ms] hover:bg-paper-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <span className="text-sm font-medium text-ink">{label}</span>
      <ExternalLink
        className="h-4 w-4 text-mute transition-colors group-hover:text-ink"
        aria-hidden="true"
      />
    </a>
  );
}

const PAGE_CLASS = 'mx-auto w-full max-w-3xl space-y-6';

export function OnboardingResultPage() {
  const navigate = useNavigate();
  const resultState = loadOnboardingResult();
  const response = resultState?.response;
  const error = resultState?.error;

  const handleStartOver = () => {
    clearOnboardingResult();
    navigate(`${ONBOARDING_BASE_PATH}/customer`, { replace: true });
  };

  const handleExit = (path: string) => {
    clearOnboardingResult();
    navigate(path, { replace: true });
  };

  if (!response && !error) {
    return (
      <div className={PAGE_CLASS}>
        <PageHeader
          eyebrow="Onboarding"
          title={tOnboarding('onboarding.result.empty.title')}
          description={tOnboarding('onboarding.result.empty.description')}
          action={
            <Button variant="primary" onClick={handleStartOver}>
              {tOnboarding('onboarding.result.empty.start')}
            </Button>
          }
        />
      </div>
    );
  }

  if (error && !response) {
    return (
      <div className={PAGE_CLASS}>
        <PageHeader
          eyebrow="Onboarding"
          title={tOnboarding('onboarding.result.error.title')}
        />
        <Card>
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        </Card>
        <div className="flex flex-wrap items-center justify-end gap-3">
          <Button variant="outline" onClick={() => handleExit('/admin')}>
            {tOnboarding('onboarding.result.actions.back_to_dashboard')}
          </Button>
          <Button variant="primary" onClick={handleStartOver}>
            {tOnboarding('onboarding.result.error.retry')}
          </Button>
        </div>
      </div>
    );
  }

  if (!response) {
    return null;
  }

  const isSuccess = response.success;
  const title = isSuccess
    ? tOnboarding('onboarding.result.success.title')
    : tOnboarding('onboarding.result.failed.title');
  const description =
    response.message ??
    (isSuccess
      ? tOnboarding('onboarding.result.success.description')
      : tOnboarding('onboarding.result.failed.description'));

  const hasLinks =
    response.urls?.activation_url ||
    response.urls?.vendor_panel_url ||
    response.urls?.shop_url;

  const isActive = response.status === 'active';

  return (
    <div className={PAGE_CLASS}>
      <PageHeader
        eyebrow="Onboarding"
        title={
          <span className="flex items-center gap-3">
            {isSuccess ? (
              <CheckCircle
                className="h-7 w-7 shrink-0 text-success"
                aria-hidden="true"
              />
            ) : (
              <XCircle
                className="h-7 w-7 shrink-0 text-danger"
                aria-hidden="true"
              />
            )}
            {title}
          </span>
        }
        description={description}
      />

      {/* Details Card */}
      <Card>
        <CardHeader>
          <CardTitle>
            {tOnboarding('onboarding.result.section.details')}
          </CardTitle>
        </CardHeader>
        <div>
          {response.sellerId && (
            <DetailRow
              label={tOnboarding('onboarding.result.details.seller_id')}
              value={response.sellerId}
              copyable
            />
          )}
          {response.status && (
            <div className="flex items-center justify-between border-b border-line py-2.5 last:border-0">
              <span className="text-sm text-ink-2">
                {tOnboarding('onboarding.result.details.status')}
              </span>
              <StatusPill variant={isActive ? 'published' : 'warning'}>
                {response.status}
              </StatusPill>
            </div>
          )}
          {response.summary && (
            <>
              <DetailRow
                label={tOnboarding(
                  'onboarding.result.details.products_created'
                )}
                value={`${response.summary.created} / ${response.summary.total}`}
              />
              {response.summary.failed > 0 && (
                <DetailRow
                  label={tOnboarding(
                    'onboarding.result.details.products_failed'
                  )}
                  value={response.summary.failed}
                />
              )}
            </>
          )}
        </div>
      </Card>

      {/* Next Steps / Links Card */}
      {hasLinks && (
        <Card>
          <CardHeader>
            <CardTitle>
              {tOnboarding('onboarding.result.section.next_steps')}
            </CardTitle>
          </CardHeader>
          <div className="space-y-3">
            {response.urls?.activation_url && (
              <LinkButton
                href={response.urls.activation_url}
                label={tOnboarding('onboarding.result.links.activation')}
              />
            )}
            {response.urls?.vendor_panel_url && (
              <LinkButton
                href={response.urls.vendor_panel_url}
                label={tOnboarding('onboarding.result.links.vendor_panel')}
              />
            )}
            {response.urls?.shop_url && (
              <LinkButton
                href={response.urls.shop_url}
                label={tOnboarding('onboarding.result.links.shop')}
              />
            )}
          </div>
        </Card>
      )}

      {/* Error details for failed */}
      {!isSuccess && response.error && (
        <Card padding="sm">
          <p className="font-mono text-sm text-danger">{response.error}</p>
        </Card>
      )}

      {/* Footer Actions */}
      <div className="flex flex-wrap items-center justify-end gap-3">
        <Button variant="outline" onClick={handleStartOver}>
          {tOnboarding('onboarding.result.actions.start_over')}
        </Button>
        <Button variant="ink" onClick={() => handleExit('/admin')}>
          {tOnboarding('onboarding.result.actions.back_to_dashboard')}
        </Button>
      </div>
    </div>
  );
}
